import {
  trackTranslationReview_AutoRedubCompleted,
  trackTranslationReview_AutoRedubFailed,
  trackTranslationReview_AutoRedubTriggered,
} from '@src/analytics/EventTracker';
import {
  dubTextChanged,
  rebuildSegmentTextsNow,
} from '@src/editor/dubbing/DubCoverageService';
import {
  dubLineIdForSegment,
  planAutoRedub,
  segmentIdsForElementIds,
} from '@src/services/dubbing/autoRedubPlan';
import { markersByLine } from '@src/services/dubbing/lineMarkers';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { reaction } from 'mobx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  DubDirection,
  DubGenerateResult,
  ResolvedDubManifest,
} from '@src/repositories/DubbingRepository';
import type { DubbingStore } from '@src/stores-v2/DubbingStore';
import type { TranslationStore } from '@src/stores-v2/TranslationStore';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('aiVoices:useAutoRedubOnCommit');

/** How long "Dub updated" stays on a segment before it returns to nothing. */
export const AUTO_REDUB_UPDATED_HOLD_MS = 2000;

export type AutoRedubStatus = 'updating' | 'updated' | 'failed';

export interface AutoRedubSegmentState {
  status: AutoRedubStatus;
  /** Why the last attempt failed, when `status` is `failed`. */
  error?: string;
  /**
   * PLAT-12034 — something the run changed besides the words, said beside
   * "Dub updated": the line's delivery markers were cleared, because the words
   * under them changed and a direction is absolute.
   */
  note?: string;
}

/**
 * The stores narrowed to what this hook reads, so a test hands it plain objects
 * and the hook never learns the shape of a whole store.
 */
export interface AutoRedubTranslationSource {
  getSegments: TranslationStore['getSegments'];
  getTranslation: TranslationStore['getTranslation'];
  isSaving: TranslationStore['isSaving'];
  hasPendingEdits: TranslationStore['hasPendingEdits'];
  lastSavedAtFor: TranslationStore['lastSavedAtFor'];
}

export interface AutoRedubRunner {
  readonly activeRun: DubbingStore['activeRun'];
  startRun: DubbingStore['startRun'];
}

export interface UseAutoRedubOnCommitParams {
  /** Off ⇒ the hook is inert: commits are ignored, nothing is tracked. */
  enabled: boolean;
  storyId: string | null;
  /** BCP-47 code of the reviewed language. */
  language: string;
  /** Human caption of that language, for the run's chrome. */
  languageCaption: string;
  translation: AutoRedubTranslationSource;
  dubbing: AutoRedubRunner;
  fetchManifest: (params: {
    storyId: string;
    language: string;
  }) => Promise<ResolvedDubManifest | null>;
  /** The transcript the translation's element ids point at, read when needed. */
  getElements: () => TranscriptElement[];
  /** For the run's time estimate; omit when unknown. */
  videoDurationSec?: number;
  /** A run landed (with or without failed lines): re-read the dub. */
  onRunCompleted?: (result: DubGenerateResult) => void;
}

export interface UseAutoRedubOnCommitResult {
  /**
   * A translation edit over these source-element ids was committed. Every
   * commit path reports the ids it covered (inline field, popover, span
   * replace, directed re-translate, Changes-panel revert) — the hook resolves
   * them to segments.
   */
  notifyCommit: (elementIds: readonly string[]) => void;
  /** Re-run one failed segment, whatever its text says. */
  retry: (segmentId: string) => void;
  /**
   * PLAT-12034 — re-dub one dub line (`p_` id) under a direction, whatever its
   * text says: a delivery-marker chip was removed, so the line's new direction
   * is its remaining markers (`null` = no markers, an undirected run). Goes out
   * on its own run, since a direction covers a whole run.
   */
  redubLine: (lineId: string, direction: DubDirection | null) => void;
  /** Per translation segment id (`TranslationStore.getSegments`). */
  stateBySegment: ReadonlyMap<string, AutoRedubSegmentState>;
}

/**
 * PLAT-11678 — when a translation edit is saved for a language that has a dub,
 * re-dub the segment it touched, with no further click.
 *
 * Timing, in three rules the ticket's story plan fixes (DS1–DS3):
 *
 * 1. **The trigger is the persist landing, not the keystroke.** The store
 *    debounces saves (2 s) and the engine voices the SAVED text, so a run
 *    started at commit would record the old words. Commits only collect
 *    segments and mark them `updating`; the run goes out when
 *    `lastSavedAtFor(language)` moves with nothing left in flight. The save
 *    debounce is therefore the coalescing window: two commits on one sentence
 *    within it cost one synthesis.
 * 2. **One queue, one run.** `DubbingStore.startRun` aborts whatever is
 *    running, so this never starts while `activeRun` is set — ours or the
 *    user's own full dub. Pending segments wait and go out together.
 * 3. **Recording still matches ⇒ nothing.** Before any call, each pending
 *    segment's rebuilt text is compared with the words its recording was voiced
 *    from (`DubCoverageService`, the same rule as the out-of-date warning). A
 *    revert to a prior value, or a save that changed nothing spoken, leaves the
 *    dub alone.
 *
 * State is per translation segment and lives here, not in a store: it is
 * transient review UI, gone when the review closes.
 */
export function useAutoRedubOnCommit(
  params: UseAutoRedubOnCommitParams,
): UseAutoRedubOnCommitResult {
  const {
    enabled,
    storyId,
    language,
    languageCaption,
    translation,
    dubbing,
    fetchManifest,
    getElements,
    videoDurationSec,
    onRunCompleted,
  } = params;

  const [stateBySegment, setStateBySegment] = useState<
    ReadonlyMap<string, AutoRedubSegmentState>
  >(() => new Map());

  // Translation segment ids waiting for the next idle moment, and the subset
  // the reviewer forced through with Retry (text check skipped).
  const pendingRef = useRef(new Set<string>());
  const forcedRef = useRef(new Set<string>());
  // PLAT-12034 — dub lines queued with a direction of their own. One run
  // carries one direction, so such a line goes out alone; the rest wait.
  const directionByLineRef = useRef(new Map<string, DubDirection | null>());
  // PLAT-12034 — a directed line whose run failed, by segment: Retry sends it
  // under the same direction. Kept apart from the queue so a text edit on the
  // line instead goes out undirected, not under markup over its old words.
  const retryDirectionRef = useRef(
    new Map<string, { lineId: string; direction: DubDirection | null }>(),
  );
  const inFlightRef = useRef(false);
  // Bumped whenever the review's target changes (language switch or the flag
  // toggling — the reset effect below). A flush captures this at entry; one that
  // resolves after the target moved must not paint its result, re-queue, or name
  // a run, or it would land the OLD language's audio/state on the NEW one.
  const generationRef = useRef(0);
  // Whether a persist has landed since the last flush attempt. A commit that
  // arrives while the store is idle and already saved (a revert applies at
  // once) would otherwise wait for a save that never comes.
  const savedSinceCommitRef = useRef(false);
  const holdTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  const mountedRef = useRef(true);

  // Read through refs so the flush below never re-binds on a new identity —
  // the host passes inline functions.
  const latest = useRef({
    fetchManifest,
    getElements,
    onRunCompleted,
    videoDurationSec,
    languageCaption,
    storyId,
  });
  latest.current = {
    fetchManifest,
    getElements,
    onRunCompleted,
    videoDurationSec,
    languageCaption,
    storyId,
  };

  const setSegmentState = useCallback(
    (segmentIds: readonly string[], next: AutoRedubSegmentState | null) => {
      if (segmentIds.length === 0 || !mountedRef.current) return;
      // Outside the updater: an updater runs lazily on the next render, and
      // clearing from inside it would cancel the hold timer scheduled below.
      for (const id of segmentIds) {
        const timer = holdTimersRef.current.get(id);
        if (timer) {
          clearTimeout(timer);
          holdTimersRef.current.delete(id);
        }
      }
      setStateBySegment(prev => {
        const map = new Map(prev);
        for (const id of segmentIds) {
          if (next) map.set(id, next);
          else map.delete(id);
        }
        return map;
      });
      if (next?.status === 'updated') {
        for (const id of segmentIds) {
          holdTimersRef.current.set(
            id,
            setTimeout(() => {
              holdTimersRef.current.delete(id);
              if (!mountedRef.current) return;
              setStateBySegment(prev => {
                if (prev.get(id)?.status !== 'updated') return prev;
                const map = new Map(prev);
                map.delete(id);
                return map;
              });
            }, AUTO_REDUB_UPDATED_HOLD_MS),
          );
        }
      }
    },
    [],
  );

  const flush = useCallback(async () => {
    if (inFlightRef.current || !mountedRef.current) return;
    if (pendingRef.current.size === 0) return;
    // Rule 1: nothing goes out over an unsaved translation — neither one in
    // flight nor one the debounced save has not sent yet.
    if (
      translation.isSaving(language) ||
      translation.hasPendingEdits(language)
    ) {
      return;
    }
    // Rule 2: nothing goes out over another run.
    if (dubbing.activeRun) return;
    const currentStoryId = latest.current.storyId;
    if (!currentStoryId) return;

    inFlightRef.current = true;
    const generation = generationRef.current;
    const segmentIds = [...pendingRef.current];
    const forcedSegmentIds = [...forcedRef.current];
    pendingRef.current.clear();
    forcedRef.current.clear();
    savedSinceCommitRef.current = false;
    // Set once this flush takes a directed line off the queue; a failure
    // hands it to Retry.
    let directed: {
      lineId: string;
      direction: DubDirection | null;
      segmentIds: string[];
    } | null = null;
    const keepForRetry = () => {
      if (!directed) return;
      for (const id of directed.segmentIds) {
        retryDirectionRef.current.set(id, {
          lineId: directed.lineId,
          direction: directed.direction,
        });
      }
    };
    for (const id of segmentIds) retryDirectionRef.current.delete(id);

    try {
      const data = translation.getTranslation(language);
      const { textByLineId, orderKnownByLineId, sentenceByKey } =
        rebuildSegmentTextsNow(
          // ALL fragments, superseded included: a span replacement finds its
          // host sentence through the base fragments it displaced, and the
          // rebuild itself keeps only the live ones.
          data?.phrases ?? [],
          latest.current.getElements(),
        );

      // Translation segment → the line its recording is filed under. Several
      // segments can share one line (a replacement and its host), so the map
      // is line → segments and the plan works in lines.
      const segmentsByLine = new Map<string, string[]>();
      const segments = translation.getSegments(language);
      const segmentById = new Map(segments.map(s => [s.id, s]));
      // A pending segment that no longer exists (a re-tile merged/renamed it
      // between commit and flush) resolves to no line. It must be cleared, not
      // skipped: left in `updating` it would spin on "Updating dub…" forever.
      const droppedSegmentIds: string[] = [];
      for (const segmentId of segmentIds) {
        const segment = segmentById.get(segmentId);
        const lineId = segment
          ? dubLineIdForSegment(segment, sentenceByKey)
          : null;
        if (!lineId) {
          droppedSegmentIds.push(segmentId);
          continue;
        }
        const list = segmentsByLine.get(lineId) ?? [];
        list.push(segmentId);
        segmentsByLine.set(lineId, list);
      }
      const forcedLines = new Set(
        forcedSegmentIds.flatMap(segmentId => {
          const segment = segmentById.get(segmentId);
          const lineId = segment
            ? dubLineIdForSegment(segment, sentenceByKey)
            : null;
          return lineId ? [lineId] : [];
        }),
      );
      // PLAT-12034 — a line queued with its own direction runs alone: every
      // other pending line goes back to the queue for the flush after this one.
      let direction: DubDirection | null | undefined;
      const directedLine = [...segmentsByLine.keys()].find(lineId =>
        directionByLineRef.current.has(lineId),
      );
      if (directedLine) {
        direction = directionByLineRef.current.get(directedLine);
        directionByLineRef.current.delete(directedLine);
        directed = {
          lineId: directedLine,
          direction: direction ?? null,
          segmentIds: segmentsByLine.get(directedLine) ?? [],
        };
        for (const [lineId, ids] of segmentsByLine) {
          if (lineId === directedLine) continue;
          for (const id of ids) {
            pendingRef.current.add(id);
            if (forcedSegmentIds.includes(id)) forcedRef.current.add(id);
          }
          segmentsByLine.delete(lineId);
        }
        forcedLines.clear();
        forcedLines.add(directedLine);
      }

      const resolved = await latest.current.fetchManifest({
        storyId: currentStoryId,
        language,
      });
      // The review's target moved while the manifest was in flight (a language
      // switch, or the flag going off): this run belongs to the old language.
      // Abandon before touching any state or naming a run under it — the finally
      // re-flushes through the CURRENT closure for whatever is pending now.
      if (generation !== generationRef.current) return;
      const plan = planAutoRedub({
        pendingLineIds: [...segmentsByLine.keys()],
        manifest: resolved?.manifest ?? null,
        textNowByLineId: textByLineId,
        changed: (lineId, dubbedFrom, now) =>
          dubTextChanged(
            dubbedFrom,
            now,
            orderKnownByLineId.get(lineId) ?? true,
          ),
        forceLineIds: [...forcedLines],
      });

      const segmentsOf = (lineIds: readonly string[]) =>
        lineIds.flatMap(lineId => segmentsByLine.get(lineId) ?? []);

      // Nothing to voice for these: the recording already says these words, or
      // there is no recording to update. Their "updating" comes off — as does a
      // dropped segment's, which resolved to no line at all.
      setSegmentState(
        [
          ...segmentsOf([...plan.unchanged, ...plan.notDubbed]),
          ...droppedSegmentIds,
        ],
        null,
      );
      setSegmentState(segmentsOf(plan.noVoice), {
        status: 'failed',
        error: 'No AI voice is set for this speaker.',
      });

      if (plan.lineIds.length === 0) return;

      trackTranslationReview_AutoRedubTriggered({
        story_id: currentStoryId,
        language,
        segment_count: plan.lineIds.length,
        unchanged_count: plan.unchanged.length,
      });
      const speakerCount = Object.keys(plan.voiceBySpeaker).length;
      // PLAT-12034 — lines this run re-records WITHOUT a direction although
      // their recording had delivery markers: a direction is absolute, so the
      // markers go, and the row says so rather than the chips just vanishing.
      const markersClearedLines = direction
        ? new Set<string>()
        : new Set(
            plan.lineIds.filter(lineId =>
              markersByLine(resolved?.manifest ?? null).has(lineId),
            ),
          );
      let result: DubGenerateResult;
      try {
        result = await dubbing.startRun(
          {
            storyId: currentStoryId,
            targetLanguage: language,
            segmentIds: plan.lineIds,
            voiceBySpeaker: plan.voiceBySpeaker,
            regenerate: true,
            ...(direction ? { direction } : {}),
          },
          {
            languageCaption: latest.current.languageCaption,
            automatic: true,
            etaMs: estimateScopedRunMs(
              plan.lineIds.length,
              speakerCount,
              latest.current.videoDurationSec,
            ),
          },
        );
      } catch (err) {
        // Superseded by another run (the user's own, or our next flush) —
        // the segments stay pending for that one; nothing to report.
        if (isAbortError(err)) {
          // Only re-queue when this flush is still current: after a language
          // switch these segments belong to the old language, and re-adding
          // them would pollute the new language's queue.
          if (generation === generationRef.current) {
            for (const id of segmentsOf(plan.lineIds)) {
              pendingRef.current.add(id);
            }
            // PLAT-12034 — a directed line goes back with its direction and
            // forced: its words did not change, so without them the next
            // flush would skip it, or run it undirected and clear its markers.
            if (directedLine && direction !== undefined) {
              directionByLineRef.current.set(directedLine, direction);
              for (const id of segmentsOf([directedLine])) {
                forcedRef.current.add(id);
              }
            }
            // Re-open the flush gate. This flush cleared `savedSinceCommitRef`
            // when it started, and a superseding run ending does NOT move
            // `lastSavedAtFor`, so the reaction (rule 1+2) would never re-fire
            // for these re-queued segments — they'd sit on "Updating dub…" for
            // the rest of the session with no Retry. Their text is already
            // saved; they only need the next idle moment.
            savedSinceCommitRef.current = true;
          }
          return;
        }
        if (generation !== generationRef.current) return;
        keepForRetry();
        const info = extractErrorInfo(err);
        logger.error('Auto re-dub failed', { language, ...info });
        setSegmentState(segmentsOf(plan.lineIds), {
          status: 'failed',
          error: info.errorMessage || 'Dub generation failed.',
        });
        trackTranslationReview_AutoRedubFailed({
          story_id: currentStoryId,
          language,
          segment_count: plan.lineIds.length,
          error_message: info.errorMessage || 'unknown',
        });
        return;
      }

      const failedByLine = new Map(
        result.coverage.failed.map(f => [f.segmentId, f.error]),
      );
      const skippedByLine = new Map(
        result.coverage.skipped.map(s => [s.segmentId, s.reason]),
      );
      // The run landed after an await; if the target moved while it ran, its
      // audio belongs to the old language — don't paint its result on the new.
      if (generation !== generationRef.current) return;
      const done: string[] = [];
      const doneMarkersCleared: string[] = [];
      for (const lineId of plan.lineIds) {
        const failure = failedByLine.get(lineId) ?? skippedByLine.get(lineId);
        if (failure !== undefined) {
          if (lineId === directed?.lineId) keepForRetry();
          setSegmentState(segmentsOf([lineId]), {
            status: 'failed',
            error: failure,
          });
        } else if (markersClearedLines.has(lineId)) {
          doneMarkersCleared.push(...segmentsOf([lineId]));
        } else {
          done.push(...segmentsOf([lineId]));
        }
      }
      setSegmentState(done, { status: 'updated' });
      setSegmentState(doneMarkersCleared, {
        status: 'updated',
        note: 'markers cleared',
      });
      trackTranslationReview_AutoRedubCompleted({
        story_id: currentStoryId,
        language,
        run_id: result.runId,
        synthesized_count: result.coverage.synthesized.length,
        reused_count: result.coverage.reused.length,
        failed_count: result.coverage.failed.length,
      });
      latest.current.onRunCompleted?.(result);
    } catch (err) {
      // The manifest read or the text rebuild threw: not the engine's fault,
      // but the segments cannot be left spinning.
      if (generation !== generationRef.current) return;
      keepForRetry();
      const info = extractErrorInfo(err);
      logger.error('Auto re-dub could not be planned', { language, ...info });
      setSegmentState(segmentIds, {
        status: 'failed',
        error: info.errorMessage || 'Could not check the dub.',
      });
    } finally {
      inFlightRef.current = false;
      // Commits that arrived during this run go out next — through the CURRENT
      // closure (`flushRef`), never this possibly-stale one, so a commit for a
      // language switched to mid-run is named under the right target, not this
      // flush's.
      if (pendingRef.current.size > 0) void flushRef.current();
    }
  }, [translation, dubbing, language, setSegmentState]);

  // Always the latest `flush`; the in-flight flush's `finally` re-flushes
  // through this so it runs the current language's closure, not its own.
  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Rule 1 + 2 as one reaction: fire when a persist lands or a run ends.
  useEffect(() => {
    if (!enabled) return;
    const dispose = reaction(
      () => ({
        saving: translation.isSaving(language),
        pending: translation.hasPendingEdits(language),
        savedAt: translation.lastSavedAtFor(language),
        running: dubbing.activeRun !== null,
      }),
      (next, prev) => {
        if (next.savedAt !== prev.savedAt) savedSinceCommitRef.current = true;
        if (
          !next.saving &&
          !next.pending &&
          !next.running &&
          savedSinceCommitRef.current
        ) {
          void flush();
        }
      },
    );
    return dispose;
  }, [enabled, translation, dubbing, language, flush]);

  const notifyCommit = useCallback(
    (elementIds: readonly string[]) => {
      if (!enabled || elementIds.length === 0) return;
      const segmentIds = segmentIdsForElementIds(
        translation.getSegments(language),
        elementIds,
      );
      if (segmentIds.length === 0) return;
      for (const id of segmentIds) pendingRef.current.add(id);
      setSegmentState(segmentIds, { status: 'updating' });
      // A revert or an override applies without a debounced save: if nothing
      // is pending or in flight the reaction has nothing to see, so try now;
      // `flush` re-checks every gate itself.
      if (
        !translation.isSaving(language) &&
        !translation.hasPendingEdits(language)
      ) {
        savedSinceCommitRef.current = true;
        void flush();
      }
    },
    [enabled, translation, language, setSegmentState, flush],
  );

  const retry = useCallback(
    (segmentId: string) => {
      if (!enabled) return;
      const kept = retryDirectionRef.current.get(segmentId);
      if (kept) directionByLineRef.current.set(kept.lineId, kept.direction);
      pendingRef.current.add(segmentId);
      forcedRef.current.add(segmentId);
      setSegmentState([segmentId], { status: 'updating' });
      savedSinceCommitRef.current = true;
      void flush();
    },
    [enabled, setSegmentState, flush],
  );

  // PLAT-12034 — the line's translation segments are what the review's rows
  // key their status on, so they are resolved here, the way the flush resolves
  // them, and all marked updating together.
  const redubLine = useCallback(
    (lineId: string, direction: DubDirection | null) => {
      if (!enabled) return;
      const data = translation.getTranslation(language);
      const { sentenceByKey } = rebuildSegmentTextsNow(
        data?.phrases ?? [],
        latest.current.getElements(),
      );
      const segmentIds = translation
        .getSegments(language)
        .filter(
          segment => dubLineIdForSegment(segment, sentenceByKey) === lineId,
        )
        .map(segment => segment.id);
      if (segmentIds.length === 0) return;
      for (const id of segmentIds) {
        pendingRef.current.add(id);
        forcedRef.current.add(id);
      }
      directionByLineRef.current.set(lineId, direction);
      setSegmentState(segmentIds, { status: 'updating' });
      savedSinceCommitRef.current = true;
      void flush();
    },
    [enabled, translation, language, setSegmentState, flush],
  );

  // A language switch or the flag going off drops everything: the review is
  // now about a different dub. Bump the generation so a flush still in flight
  // for the old target abandons its result instead of painting it here, and
  // clear the save gate so a stale "ready to go" cannot fire against the new
  // language. `inFlightRef` is deliberately left alone — the in-flight flush
  // owns it and clears it in its own `finally`; resetting it here would let a
  // second flush start concurrently.
  useEffect(() => {
    generationRef.current += 1;
    savedSinceCommitRef.current = false;
    pendingRef.current.clear();
    forcedRef.current.clear();
    directionByLineRef.current.clear();
    retryDirectionRef.current.clear();
    for (const timer of holdTimersRef.current.values()) clearTimeout(timer);
    holdTimersRef.current.clear();
    setStateBySegment(new Map());
  }, [language, enabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const timer of holdTimersRef.current.values()) clearTimeout(timer);
      holdTimersRef.current.clear();
    };
  }, []);

  return useMemo(
    () => ({ notifyCommit, retry, redubLine, stateBySegment }),
    [notifyCommit, retry, redubLine, stateBySegment],
  );
}

/**
 * A scoped run is a handful of lines, not a story: estimate by the lines it
 * names rather than the video's length, but never below the engine's own
 * floor for one speaker.
 */
function estimateScopedRunMs(
  lineCount: number,
  speakerCount: number,
  videoDurationSec: number | undefined,
): number {
  const PER_LINE_MS = 4000;
  const PER_SPEAKER_MS = 3000;
  const base = 6000 + lineCount * PER_LINE_MS + speakerCount * PER_SPEAKER_MS;
  if (videoDurationSec === undefined) return base;
  // Never claim to be slower than a whole-story run would be.
  return Math.min(base, 6000 + videoDurationSec * 250 + speakerCount * 3000);
}
